// External library imports
import React, { FC, memo } from 'react';
import clsx from 'clsx';
import Image, { ImageLoaderProps } from 'next/image';
import Head from 'next/head';

// Internal library imports
import { ImageProps, RenditionProps } from './CobrandImage.types';

/**
 * Custom loader for Next.js Image component
 * Returns the source URL as-is without optimization
 * @param src - Image source URL
 * @returns Unmodified source URL
 */
const customLoader = ({ src }: ImageLoaderProps): string => {
  return src;
};

/**
 * CobrandImage component displays responsive images with multiple renditions
 * Supports lazy/eager loading and preloading for performance optimization
 * Used for displaying credit card images across different viewport sizes
 */
export const CobrandImage: FC<ImageProps> = memo<ImageProps>((props: ImageProps) => {
  // Destructure props with default values
  const { title, defaultImageURL, customClass, renditions, altText = 'Card Overview Image', loading = 'lazy' } = props;

  return (
    <>
      {defaultImageURL && (
        <Head>
          <link rel="preload" as="image" href={defaultImageURL} />
        </Head>
      )}
      <picture data-component-name="m-cobrand-CobrandImage" data-testid="cobrand-CobrandImage">
        {renditions?.map((rendition: RenditionProps, index: number) => {
          const { renditionPath, mediaValue, mediaQuery } = rendition;

          if (!renditionPath || !mediaQuery || !mediaValue) {
            return null;
          }

          const media = `(${mediaQuery}: ${mediaValue})`;

          return (
            <source
              key={`rendition-${index}`}
              media={media}
              data-srcset={renditionPath}
              data-testid={`source-img-${index}`}
            />
          );
        })}

        {defaultImageURL && (
          <Image
            loader={customLoader}
            src={defaultImageURL}
            className={clsx('image', loading, customClass)}
            alt={altText}
            title={title}
            data-src={defaultImageURL}
            loading={loading}
          />
        )}
      </picture>
    </>
  );
});

CobrandImage.displayName = 'CobrandImage';
